import { ReactElement } from 'react'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { NextPageWithLayout } from '../_app'
import { DefaultSeo } from 'next-seo'
import SEO from '../../next-seo.config'
import PackageCard from '../../components/member/PackageCard'
import { useUser } from '../../hooks/users/useUser'
import { ProductResponse } from '../../types/products/product-response'
import { getProducts } from '../../api/products/products'
import Layout from '../../components/layout'
import { useDeviceData } from '../../hooks/braintree/useDeviceData'
import { useCreditAmount } from '../../hooks/currencies/useCurrencies'
import GuestNotification from '../../components/home/GuestNotification'
import { SessionStatusEnum } from '../../common/enums/session-status.enum'
import { useSession } from 'next-auth/react'
import { getRevalidateInterval } from '../../common/utils/common.utils'

const Balance: NextPageWithLayout<{
  products: ProductResponse[]
}> = ({ products }) => {
  useDeviceData()
  const { user } = useUser()
  const { yourCreditString } = useCreditAmount()
  const { status } = useSession()

  return (
    <>
      <DefaultSeo
        {...SEO}
        title={'TapIn Reload'}
        description={
          'TapIn Reload is the easy way to add funds to your TapIn balance. Add funds using your debit, credit, or prepaid gift card and then use your balance toward games.'
        }
      />
      <div
        className={'relative mx-2 mb-0 flex flex-col items-center rounded-xl'}
      >
        <div className={'flex flex-col items-center'}>
          {/*<h1 className={'header4 z-20 mx-4 mt-12 text-center text-[#FC38CA]'}>*/}
          {/*  Get an extra 2x bonus on all packages!*/}
          {/*</h1>*/}

          {/*<p*/}
          {/*  className={*/}
          {/*    'text-body-medium z-20 mt-1.5 text-center text-darul-0 py-2'*/}
          {/*  }*/}
          {/*>*/}
          {/*  ⏳ Valid until October 4, 2024 ⏳*/}
          {/*</p>*/}

          {status === SessionStatusEnum.AUTHENTICATED && (
            <h2
              className={
                'header4 text-color-primary z-20 mx-4 mt-3 text-center'
              }
            >
              Your Credit Balance: {yourCreditString}
            </h2>
          )}

          <h1
            className={
              'text-body-medium text-darul-200 z-20 mb-4 mt-1 text-center'
            }
          >
            Credits unlock matches with our highest ranked teammates
          </h1>
        </div>

        {/* Lightning Deals, limited time offers */}
        <div className={'mt-4 flex flex-col gap-4 lg:flex-row lg:flex-wrap lg:justify-center lg:items-stretch'}>
          {products
            ?.filter((product) => product.isLightningDeal)
            ?.map((product) => {
              const expiresAt = new Date()
              expiresAt.setDate(expiresAt.getDate() + 7)

              return (
                <PackageCard
                  key={product.id}
                  product={product}
                  isLightningDeal={true}
                  lightningDealExpiresAt={product.endedAt}
                />
              )})
          }
        </div>

        {/* Non-Lightning Deals */}
        <div className={'mt-4 flex flex-col gap-4 lg:flex-row lg:flex-wrap lg:justify-center lg:items-stretch'}>
          {products
            ?.filter((product) => !product.isLightningDeal)
            ?.map((product, index) => {
            return (
              <PackageCard
                key={product.id}
                product={product}
                isPopular={product.currencyUnitAmount === 250}
              />
            )
          })}
        </div>

        {user?.isGuest && (
          <div className={'hidden md:flex mt-12'}>
            <GuestNotification />
          </div>
        )}
      </div>
    </>
  )
}

Balance.getLayout = function getLayout(page: ReactElement) {
  return <Layout>{page}</Layout>
}

type Props = {
  locale: string
}

export async function getStaticProps({ locale }: Props) {
  const data = await getProducts()

  return {
    props: {
      ...(await serverSideTranslations(locale, ['common'])),
      products: data.sort(
        (a, b) => (a.creditAmount ?? 0) - (b.creditAmount ?? 0) || a.id - b.id,
      ),
    },
    revalidate: getRevalidateInterval(),
  }
}

export default Balance
